import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Snowflake component for Arctic Vault
const Snowflake = ({ delay, duration, left }: { delay: number; duration: number; left: number }) => (
  <motion.div
    className="absolute w-1 h-1 bg-blue-200/40 rounded-full"
    style={{ left: `${left}%` }}
    initial={{ y: -10, opacity: 0 }}
    animate={{
      y: '100vh',
      opacity: [0, 1, 1, 0],
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: 'linear'
    }}
  />
);

// Spotlight component for Stage
const Spotlight = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <motion.div
      className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[500px]"
      style={{
        background: 'conic-gradient(from 180deg at 50% 0%, transparent 45%, rgba(251, 191, 36, 0.1) 50%, transparent 55%)',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3, duration: 0.8 }}
    />
  </div>
);

const LinkedInWrapped = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scanIndex, setScanIndex] = useState(0);
  const [scanComplete, setScanComplete] = useState(false);

  const shippedProjects = [
    { name: 'AI Terminal', desc: 'Terminal-based AI Course', color: 'indigo' },
    { name: 'MCP Jest', desc: 'MCP Testing Framework', color: 'red' },
    { name: 'OmniMCP', desc: 'Universal MCP Client', color: 'teal' },
    { name: 'AgentX', desc: 'Universal Agent Framework', color: 'violet' },
    { name: 'AI UI Kit', desc: 'React AI Components', color: 'pink' },
    { name: 'GenAI Hooks', desc: 'React LLM Hooks', color: 'emerald' },
    { name: 'Sundae', desc: 'macOS Voice Assistant', color: 'amber' },
    { name: 'IdeaBrew', desc: 'Voice to MVP', color: 'cyan' },
    { name: 'InFlash', desc: 'Thought Capture', color: 'blue' },
    { name: 'Clarity', desc: 'AI Email Triage', color: 'rose' },
    { name: 'SketchFlow', desc: 'Minimalist Canvas', color: 'slate' },
    { name: 'DDIY', desc: 'No-code Agent Builder', color: 'yellow' },
  ];

  const slides = [
    'hook',
    'origin',
    'open-source',
    'gsoc',
    'founding',
    'advisory',
    'unengineered',
    'scale',
    'vault',
    'stage',
    'ships',
    'proof',
  ];

  const SCAN_INTERVAL_MS = 280;
  const SCAN_WRAP_DELAY_MS = 600;

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < rect.width / 3) {
      prevSlide();
    } else {
      nextSlide();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'Escape') navigate('/');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide]);

  useEffect(() => {
    if (slides[currentSlide] !== 'ships') return;

    setScanIndex(0);
    setScanComplete(false);

    const projectCount = shippedProjects.length;
    let current = 0;
    let interval: ReturnType<typeof setInterval> | undefined;

    const advance = () => {
      current += 1;
      if (current >= projectCount) {
        setScanComplete(true);
        if (interval) clearInterval(interval);
        return;
      }
      setScanIndex(current);
    };

    interval = setInterval(advance, SCAN_INTERVAL_MS);
    const finishTimer = setTimeout(() => {
      setScanComplete(true);
      if (interval) clearInterval(interval);
    }, projectCount * SCAN_INTERVAL_MS + SCAN_WRAP_DELAY_MS);

    return () => {
      if (interval) clearInterval(interval);
      clearTimeout(finishTimer);
    };
  }, [currentSlide]);

  const slideVariants = {
    enter: { opacity: 0, y: 20 },
    center: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  const renderSlide = () => {
    switch (slides[currentSlide]) {
      // Slide 1: Hook - v4 "Neon Velocity Ultimate" (Wrapped Style + Nav Hints)
      case 'hook':
        return (
          <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-4 text-center relative overflow-hidden font-sans cursor-none">
            {/* Background: Kinetic Gradient Mesh */}
            <div className="absolute inset-0 overflow-hidden">
              <motion.div 
                className="absolute inset-[-50%] bg-[conic-gradient(from_0deg_at_50%_50%,#ff0080,#7928ca,#ff0080)] opacity-30 blur-[100px]"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              />
              <motion.div 
                className="absolute inset-[-50%] bg-[conic-gradient(from_180deg_at_50%_50%,#0070f3,#00dfd8,#0070f3)] opacity-30 blur-[100px] mix-blend-screen"
                animate={{ rotate: -360 }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              />
              <div className="absolute inset-0 bg-black/40 backdrop-blur-3xl" />
              
              {/* Simulated Audio Visualizer Bars */}
              <div className="absolute bottom-0 left-0 right-0 h-32 flex items-end justify-center gap-1 opacity-20">
                {[...Array(20)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-4 bg-white rounded-t-full"
                    animate={{ height: [20, Math.random() * 100 + 20, 20] }}
                    transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.05, ease: "easeInOut" }}
                  />
                ))}
              </div>
            </div>

            {/* Top Branding Pill */}
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 3.2, type: "spring" }}
              className="absolute top-8 z-20"
            >
              <div className="flex flex-col items-center gap-2">
                <div className="px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center gap-2 shadow-[0_0_20px_rgba(255,255,255,0.2)]">
                  <span className="w-2 h-2 rounded-full bg-[#ff0080] animate-pulse" />
                  <span className="text-[10px] font-black tracking-widest text-white">CAREER WRAPPED</span>
                </div>
                <span className="text-[9px] font-mono text-white/40 tracking-[0.2em]">2020 - PRESENT</span>
              </div>
            </motion.div>

            {/* Kinetic Typography Sequence */}
            <div className="relative z-10 flex flex-col items-center justify-center w-full h-full">
              
              {/* Phase 1: Rapid Fire Context (The Build Up) */}
              <motion.div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                initial={{ opacity: 1 }}
                animate={{ opacity: 0 }}
                transition={{ delay: 2.5, duration: 0.1 }}
              >
                {['LATE NIGHTS', 'SHIPMENTS', 'COFFEE', 'BUGS', 'DEPLOY'].map((word, i) => (
                  <motion.span
                    key={i}
                    className="absolute text-6xl sm:text-9xl font-black text-white tracking-tighter uppercase"
                    initial={{ opacity: 0, scale: 0.5, rotate: Math.random() * 20 - 10 }}
                    animate={{ opacity: [0, 1, 0], scale: [0.8, 1.5, 2], rotate: Math.random() * 20 - 10 }}
                    transition={{ 
                      duration: 0.4, 
                      delay: i * 0.4,
                      times: [0, 0.5, 1]
                    }}
                  >
                    {word}
                  </motion.span>
                ))}
              </motion.div>

              {/* Phase 2: The Drop (Main Reveal) */}
              <motion.div
                initial={{ scale: 0.1, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 2.6, type: "spring", stiffness: 200, damping: 20 }}
                className="relative perspective-1000"
              >
                {/* Particle Explosion */}
                {[...Array(12)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute top-1/2 left-1/2 w-2 h-2 bg-white rounded-full"
                    initial={{ scale: 0 }}
                    animate={{ 
                      scale: [0, 1, 0], 
                      x: [0, (Math.random() - 0.5) * 400], 
                      y: [0, (Math.random() - 0.5) * 400] 
                    }}
                    transition={{ delay: 2.6, duration: 1, ease: "easeOut" }}
                  />
                ))}

                {/* Glitch/Shake Effect Container */}
                <motion.div
                  animate={{ x: [-5, 5, -5, 5, 0], y: [-5, 5, -5, 5, 0] }}
                  transition={{ delay: 2.6, duration: 0.4 }}
                  className="relative group"
                >
                  <h1 className="text-[12rem] sm:text-[16rem] leading-none font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-white/50 tracking-tighter -rotate-6 mix-blend-overlay group-hover:scale-105 transition-transform duration-300">
                    6
                  </h1>
                  <h1 className="absolute top-0 left-0 text-[12rem] sm:text-[16rem] leading-none font-black text-transparent bg-clip-text bg-gradient-to-b from-[#ff0080] to-[#7928ca] tracking-tighter -rotate-6 blur-sm opacity-50 animate-pulse">
                    6
                  </h1>
                </motion.div>

                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 3, type: "spring" }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-24 sm:mt-32 w-full"
                >
                  <div className="bg-white text-black text-4xl sm:text-6xl font-black px-6 py-2 -rotate-3 inline-block transform hover:scale-110 transition-transform cursor-pointer shadow-[0_0_50px_rgba(255,255,255,0.5)]">
                    YEARS
                  </div>
                </motion.div>
              </motion.div>

              {/* Phase 3: Context Ticker */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 3.5 }}
                className="absolute bottom-8 w-full overflow-hidden"
              >
                <motion.div
                  className="flex whitespace-nowrap gap-8"
                  animate={{ x: [0, -1000] }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  {[...Array(10)].map((_, i) => (
                    <span key={i} className="text-xl font-bold text-white/20 font-mono">
                      ENGINEERING • ARCHITECTURE • LEADERSHIP •
                    </span>
                  ))}
                </motion.div>
              </motion.div>
            </div>

            {/* Navigation Hints */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 4 }}
              className="absolute bottom-8 z-20 flex flex-col items-center gap-2"
            >
              <div className="flex items-center gap-4 text-white/60 font-mono text-xs tracking-widest bg-black/20 backdrop-blur-sm px-4 py-2 rounded-full border border-white/5">
                <span className="animate-pulse">←</span>
                <span>USE ARROW KEYS</span>
                <span className="animate-pulse">→</span>
              </div>
              <p className="text-[9px] text-white/20 uppercase tracking-[0.3em]">or tap to navigate</p>
            </motion.div>

            {/* Click to Start Overlay (Fades out) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0] }}
              transition={{ duration: 2, delay: 1, times: [0, 0.2, 1] }}
              className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none bg-black/80"
            >
              <span className="text-white font-black tracking-widest text-xl">INITIALIZING...</span>
            </motion.div>
          </div>
        );

      // Slide 2: Origin - HaldwaniConnect (Local Ecosystem Platform)
      case 'origin':
        return (
          <div className="min-h-screen bg-gradient-to-br from-[#F5E6D3] via-[#E8D5C4] to-[#D4A574] flex flex-col items-center justify-center px-4 text-center relative overflow-hidden">
            {/* Floating business category icons */}
            {[
              { emoji: '🍕', left: '8%', top: '15%', delay: 0 },
              { emoji: '🏪', left: '85%', top: '20%', delay: 0.3 },
              { emoji: '💇', left: '12%', top: '70%', delay: 0.6 },
              { emoji: '🔧', left: '82%', top: '65%', delay: 0.2 },
              { emoji: '🏥', left: '5%', top: '45%', delay: 0.8 },
              { emoji: '📚', left: '90%', top: '40%', delay: 0.4 },
              { emoji: '🛒', left: '15%', top: '85%', delay: 0.5 },
              { emoji: '☕', left: '78%', top: '80%', delay: 0.7 },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="absolute text-2xl"
                style={{ left: item.left, top: item.top }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: 0.6,
                  scale: 1,
                  y: [0, -10, 0],
                }}
                transition={{
                  opacity: { delay: item.delay, duration: 0.5 },
                  scale: { delay: item.delay, duration: 0.5 },
                  y: { delay: item.delay + 0.5, duration: 3, repeat: Infinity },
                }}
              >
                {item.emoji}
              </motion.div>
            ))}

            {/* Decorative mountain silhouette for Uttarakhand vibe */}
            <div className="absolute bottom-0 left-0 right-0 h-32 opacity-10">
              <svg viewBox="0 0 1200 200" className="w-full h-full" preserveAspectRatio="none">
                <path d="M0,200 L0,150 L100,100 L200,140 L300,80 L400,120 L500,60 L600,100 L700,50 L800,90 L900,70 L1000,110 L1100,80 L1200,130 L1200,200 Z" fill="#8B4513" />
              </svg>
            </div>

            {/* Location badge */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 mb-4"
            >
              <div className="w-6 h-6 bg-[#C65D3B] rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </div>
              <span className="text-[#5D4037] text-sm font-medium">2016 • Haldwani, Uttarakhand</span>
            </motion.div>

            {/* Hero title */}
            <motion.h1
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="text-4xl sm:text-5xl font-black text-[#5D4037] mb-2"
            >
              HaldwaniConnect
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-[#8B6914] text-lg mb-6 font-medium"
            >
              Digital identity for every local business
            </motion.p>

            {/* Laptop mockup */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, type: 'spring' }}
              className="relative w-[600px] h-[380px] bg-[#1a1a1a] rounded-xl p-2 shadow-2xl hidden sm:block"
            >
              {/* Laptop Screen */}
              <div className="w-full h-full bg-white rounded-lg overflow-hidden flex flex-col font-sans">
                {/* Browser Header */}
                <div className="bg-[#f0f0f0] px-4 py-2 flex items-center gap-2 border-b border-[#e5e5e5]">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#FF5F56] border border-black/5" />
                    <div className="w-3 h-3 rounded-full bg-[#FFBD2E] border border-black/5" />
                    <div className="w-3 h-3 rounded-full bg-[#27C93F] border border-black/5" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="bg-white px-3 py-1 rounded-md text-[10px] text-gray-400 flex items-center gap-1 w-64 justify-center shadow-sm border border-gray-100 font-normal">
                      <span className="opacity-50">🔒</span> haldwaniconnect.com
                    </div>
                  </div>
                </div>

                {/* Web Interface */}
                <div className="flex-1 flex flex-col bg-white">
                  {/* Navbar - Lighter shade */}
                  <div className="bg-[#e67e22]/90 backdrop-blur-sm px-6 py-3 flex items-center justify-between shadow-sm z-10 border-b border-orange-200/20">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-white/90 rounded-full flex items-center justify-center shadow-sm">
                        <span className="text-orange-600 text-xs">📍</span>
                      </div>
                      <span className="text-white font-semibold tracking-tight">HaldwaniConnect</span>
                    </div>
                    <div className="flex gap-4 text-white/90 text-[10px] font-normal tracking-wide">
                      <span className="hover:text-white cursor-pointer">Directory</span>
                      <span className="hover:text-white cursor-pointer">Events</span>
                      <span className="hover:text-white cursor-pointer">About</span>
                      <span className="bg-white/20 px-2 py-0.5 rounded text-white hover:bg-white/30 transition-colors cursor-pointer">Login</span>
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 flex overflow-hidden">
                    {/* Sidebar - Lighter background */}
                    <div className="w-40 bg-[#fafafa] border-r border-gray-100 p-4 flex flex-col gap-2">
                      <p className="text-[9px] font-semibold text-gray-400 mb-1 uppercase tracking-wider">Categories</p>
                      {[
                        { emoji: '🍽️', label: 'Food & Dining' },
                        { emoji: '🏪', label: 'Shopping' },
                        { emoji: '🔧', label: 'Services' },
                        { emoji: '🏥', label: 'Healthcare' },
                        { emoji: '🎬', label: 'Entertainment' },
                      ].map((cat, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px] text-gray-500 hover:text-gray-800 hover:bg-white hover:shadow-sm p-1.5 rounded transition-all cursor-pointer group">
                          <span className="grayscale group-hover:grayscale-0 transition-all opacity-70 group-hover:opacity-100">{cat.emoji}</span>
                          <span className="font-medium">{cat.label}</span>
                        </div>
                      ))}
                    </div>

                    {/* Dashboard/Feed */}
                    <div className="flex-1 p-6 bg-white overflow-y-auto">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-sm font-semibold text-gray-700">Featured Businesses</h3>
                        <div className="bg-gray-50 border border-gray-100 rounded-full px-3 py-1.5 flex items-center gap-2 w-48">
                          <span className="text-gray-300 text-[10px]">🔍</span>
                          <span className="text-gray-400 text-[10px] font-light">Search...</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { name: 'Sharma Store', cat: 'Retail', rating: '4.8' },
                          { name: 'Gupta Electronics', cat: 'Electronics', rating: '4.5' },
                          { name: 'City Hospital', cat: 'Healthcare', rating: '4.9' },
                          { name: 'Royal Cafe', cat: 'Dining', rating: '4.7' },
                        ].map((item, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8 + i * 0.1 }}
                            className="border border-gray-100 rounded-lg p-3 hover:border-orange-100 hover:shadow-sm transition-all flex gap-3 group cursor-pointer bg-white"
                          >
                            <div className="w-10 h-10 bg-gray-50 rounded-md shrink-0 flex items-center justify-center text-lg grayscale group-hover:grayscale-0 transition-all opacity-80 group-hover:opacity-100">
                              {i === 0 ? '🏪' : i === 1 ? '📺' : i === 2 ? '🏥' : '🍽️'}
                            </div>
                           <div className="flex flex-col items-start">
                            <p className="text-[10px] font-semibold text-gray-700 group-hover:text-orange-600 transition-colors">
                              {item.name}
                            </p>
                            <p className="text-[9px] text-gray-400 font-light">
                              {item.cat}
                            </p>
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-[8px] text-orange-400">★</span>
                              <span className="text-[8px] font-medium text-gray-500">
                                {item.rating}
                              </span>
                            </div>
                          </div>

                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Mobile Fallback */}
            <motion.div
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ delay: 0.5, type: 'spring' }}
               className="relative w-[280px] h-[180px] bg-[#1a1a1a] rounded-xl p-1 shadow-2xl sm:hidden block"
            >
               <div className="w-full h-full bg-white rounded-lg overflow-hidden flex flex-col items-center justify-center text-gray-400 text-xs">
                  <span className="mb-2">💻</span>
                  Desktop View
               </div>
            </motion.div>

            {/* Features below phone */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3 }}
              className="mt-6 flex flex-wrap justify-center gap-3"
            >
              {[
                { icon: '📍', text: 'Local Directory' },
                { icon: '💬', text: 'Community' },
                { icon: '🏪', text: 'Digital Storefronts' },
              ].map((feat, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-white/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
                  <span className="text-sm">{feat.icon}</span>
                  <span className="text-[#5D4037] text-xs font-medium">{feat.text}</span>
                </div>
              ))}
            </motion.div>

            {/* Bottom tagline */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
              className="text-[#8B6914]/60 text-xs mt-4"
            >
              JustDial for my hometown • My first build at 16
            </motion.p>
          </div>
        );

      // Slide 3: Open Source - v5 "Authentic GitHub.com" (Official GitHub Style)
      case 'open-source':
        return (
          <div className="min-h-screen bg-[#0D1117] flex flex-col items-center justify-center px-6 py-12 text-center relative overflow-hidden" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif' }}>
            {/* Background: Contribution Graph Pattern */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {/* Subtle gradient glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[radial-gradient(ellipse_at_center,_rgba(56,139,253,0.15)_0%,_transparent_70%)]" />
              
              {/* Contribution Graph Squares (subtle background) */}
              <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-1 opacity-20">
                {[...Array(52)].map((_, week) => (
                  <div key={week} className="flex flex-col gap-1">
                    {[...Array(7)].map((_, day) => (
                      <motion.div
                        key={day}
                        className="w-2.5 h-2.5 rounded-sm"
                        style={{ 
                          backgroundColor: Math.random() > 0.6 ? '#238636' : Math.random() > 0.3 ? '#0E4429' : '#161B22',
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: (week * 7 + day) * 0.002 }}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Main Content */}
            <div className="relative z-10 w-full max-w-3xl flex flex-col items-center">
              
              {/* GitHub Logo + Title */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-10 flex flex-col items-center"
              >
                {/* GitHub Mark */}
                <svg height="64" viewBox="0 0 16 16" width="64" className="fill-white mb-6">
                  <path d="M8 0c4.42 0 8 3.58 8 8a8.013 8.013 0 0 1-5.45 7.59c-.4.08-.55-.17-.55-.38 0-.27.01-1.13.01-2.2 0-.75-.25-1.23-.54-1.48 1.78-.2 3.65-.88 3.65-3.95 0-.88-.31-1.59-.82-2.15.08-.2.36-1.02-.08-2.12 0 0-.67-.22-2.2.82-.64-.18-1.32-.27-2-.27-.68 0-1.36.09-2 .27-1.53-1.03-2.2-.82-2.2-.82-.44 1.1-.16 1.92-.08 2.12-.51.56-.82 1.28-.82 2.15 0 3.06 1.86 3.75 3.64 3.95-.23.2-.44.55-.51 1.07-.46.21-1.61.55-2.33-.66-.15-.24-.6-.83-1.23-.82-.67.01-.27.38.01.53.34.19.73.9.82 1.13.16.45.68 1.31 2.69.94 0 .67.01 1.3.01 1.49 0 .21-.15.45-.55.38A7.995 7.995 0 0 1 0 8c0-4.42 3.58-8 8-8Z"></path>
                </svg>
                
                <h1 className="text-4xl md:text-5xl font-semibold text-white mb-3 tracking-tight">
                  Building in Public
                </h1>
                <p className="text-[#8B949E] text-lg">
                  Since <span className="text-white font-medium">2017</span>
                </p>
              </motion.div>

              {/* Profile Card - GitHub Style */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, type: "spring" }}
                className="w-full max-w-md bg-[#161B22] rounded-xl border border-[#30363D] overflow-hidden"
              >
                {/* Header with Avatar */}
                <div className="p-6 pb-4 border-b border-[#30363D] flex items-center gap-4">
                  <img 
                    src="/github-image.jpeg" 
                    alt="josharsh" 
                    className="w-16 h-16 rounded-full object-cover border-2 border-[#30363D] shadow-lg"
                  />
                  <div className="text-left">
                    <h2 className="text-xl font-semibold text-white">josharsh</h2>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-4 divide-x divide-[#30363D] border-b border-[#30363D]">
                  {[
                    { label: 'repos', value: '152', icon: 'M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Z' },
                    { label: 'stars', value: '862', icon: 'M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.751.751 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25Z' },
                    { label: 'PRs', value: '133', icon: 'M5 3.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm0 2.122a2.25 2.25 0 1 0-1.5 0v.878A2.25 2.25 0 0 0 5.75 8.5h1.5v2.128a2.251 2.251 0 1 0 1.5 0V8.5h1.5a2.25 2.25 0 0 0 2.25-2.25v-.878a2.25 2.25 0 1 0-1.5 0v.878a.75.75 0 0 1-.75.75h-4.5A.75.75 0 0 1 5 6.25v-.878Z' },
                    { label: 'contributions', value: '3k+', icon: 'M11.93 8.5a4.002 4.002 0 0 1-7.86 0H.75a.75.75 0 0 1 0-1.5h3.32a4.002 4.002 0 0 1 7.86 0h3.32a.75.75 0 0 1 0 1.5Zm-1.43-.75a2.5 2.5 0 1 0-5 0 2.5 2.5 0 0 0 5 0Z' },
                  ].map((stat, i) => (
                    <div key={i} className="py-4 text-center">
                      <div className="text-white font-bold text-lg">{stat.value}</div>
                      <div className="text-[#8B949E] text-xs uppercase tracking-wide">{stat.label}</div>
                    </div>
                  ))}
                </div>

                {/* Featured Repos */}
                <div className="p-4 space-y-3">
                  <p className="text-[#8B949E] text-xs uppercase tracking-wider font-medium mb-3">Pinned</p>
                  {[
                    { name: '100LinesOfCode', desc: 'Projects in 100 Lines of Code', stars: '717', lang: 'Jupyter', langColor: '#DA5B0B' },
                    { name: 'OPython-Init', desc: 'Object-Oriented Programming Guide', stars: '165', lang: 'Python', langColor: '#3572A5' },
                  ].map((repo, i) => (
                    <div key={i} className="bg-[#0D1117] border border-[#30363D] rounded-lg p-3 hover:border-[#8B949E] transition-colors text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="w-4 h-4 text-[#8B949E]" fill="currentColor" viewBox="0 0 16 16"><path d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Z"/></svg>
                        <span className="text-[#58A6FF] font-semibold text-sm">{repo.name}</span>
                      </div>
                      <p className="text-[#8B949E] text-xs mb-2 pl-6">{repo.desc}</p>
                      <div className="flex items-center gap-3 text-xs text-[#8B949E] pl-6">
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: repo.langColor }} />
                          {repo.lang}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 16 16"><path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.751.751 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25Z"/></svg>
                          {repo.stars}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Contribution Graph Label */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="mt-8 flex items-center gap-2 text-[#8B949E] text-sm"
              >
                <span className="w-2 h-2 rounded-full bg-[#238636] animate-pulse" />
                <span>3,000+ contributions in the last year</span>
              </motion.div>

            </div>
          </div>
        );

      // Slide 4: GSoC - v5 "Enhanced GSoC" (More Visual Interest)
      case 'gsoc':
        return (
          <div className="min-h-screen bg-gradient-to-br from-white via-[#F8F9FA] to-[#E8F0FE] flex flex-col items-center justify-center px-6 py-12 text-center relative overflow-hidden" style={{ fontFamily: '"Google Sans", "Product Sans", system-ui, sans-serif' }}>
            {/* Background: Animated GSoC Circles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {/* Large decorative circles with more vibrant colors */}
              <motion.div 
                className="absolute -top-32 -left-32 w-96 h-96 rounded-full"
                style={{ background: 'linear-gradient(135deg, rgba(66,133,244,0.15) 0%, rgba(66,133,244,0.05) 100%)' }}
                animate={{ rotate: [0, 360], scale: [1, 1.1, 1] }}
                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              />
              <motion.div 
                className="absolute top-1/4 -right-40 w-[500px] h-[500px] rounded-full"
                style={{ background: 'linear-gradient(135deg, rgba(251,188,4,0.12) 0%, rgba(251,188,4,0.03) 100%)' }}
                animate={{ rotate: [360, 0] }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
              />
              <motion.div 
                className="absolute -bottom-40 left-1/3 w-80 h-80 rounded-full"
                style={{ background: 'linear-gradient(135deg, rgba(234,67,53,0.1) 0%, rgba(234,67,53,0.02) 100%)' }}
                animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div 
                className="absolute top-1/2 left-1/4 w-40 h-40 rounded-full bg-[#34A853]/10"
                animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              />
              
              {/* Code snippet decorations */}
              <div className="absolute top-20 right-20 text-[#4285F4]/20 font-mono text-xs hidden md:block">
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                >
                  {'{ "contributor": true }'}
                </motion.div>
              </div>
              <div className="absolute bottom-32 left-16 text-[#34A853]/20 font-mono text-xs hidden md:block">
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.5 }}
                >
                  {'git commit -m "GSoC 2020"'}
                </motion.div>
              </div>
            </div>

            {/* Main Content */}
            <div className="relative z-10 w-full max-w-3xl flex flex-col items-center">
              
              {/* GSoC Logo with glow effect */}
              <motion.div
                initial={{ scale: 0, opacity: 0, rotate: -180 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                transition={{ type: "spring", duration: 1.2, bounce: 0.4 }}
                className="mb-8 relative flex items-center justify-center"
              >
                {/* Glow behind logo */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4]/20 via-[#EA4335]/20 to-[#FBBC04]/20 blur-3xl rounded-full scale-150" />
                
                <svg width="140" height="140" viewBox="0 0 140 140" className="drop-shadow-lg relative z-10">
                  {/* Animated outer rings */}
                  <motion.circle 
                    cx="70" cy="70" r="65" fill="none" stroke="#4285F4" strokeWidth="8" strokeDasharray="95 310" strokeLinecap="round"
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    style={{ transformOrigin: 'center' }}
                  />
                  <motion.circle 
                    cx="70" cy="70" r="65" fill="none" stroke="#EA4335" strokeWidth="8" strokeDasharray="95 310" strokeDashoffset="-100" strokeLinecap="round"
                    animate={{ rotate: [0, -360] }}
                    transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                    style={{ transformOrigin: 'center' }}
                  />
                  <motion.circle 
                    cx="70" cy="70" r="65" fill="none" stroke="#FBBC04" strokeWidth="8" strokeDasharray="95 310" strokeDashoffset="-200" strokeLinecap="round"
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                    style={{ transformOrigin: 'center' }}
                  />
                  <motion.circle 
                    cx="70" cy="70" r="65" fill="none" stroke="#34A853" strokeWidth="8" strokeDasharray="95 310" strokeDashoffset="-300" strokeLinecap="round"
                    animate={{ rotate: [0, -360] }}
                    transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
                    style={{ transformOrigin: 'center' }}
                  />
                  {/* Inner Google G - properly centered */}
                  <g transform="translate(46, 46)">
                    <path fill="#4285F4" d="M43.2 24c0-1.5-.14-2.94-.38-4.32H24v8.16h10.8c-.5 2.62-2 4.84-4.24 6.34v5.32h6.86c4-3.68 6.3-9.1 6.3-15.5z" transform="scale(1)" />
                    <path fill="#34A853" d="M24 44c5.72 0 10.5-1.88 14-5.12l-6.86-5.32c-1.88 1.26-4.28 2.04-7.14 2.04-5.5 0-10.16-3.72-11.82-8.7H5.1v5.46C8.56 39.42 15.78 44 24 44z" transform="scale(1)" />
                    <path fill="#FBBC04" d="M12.18 27.08c-.42-1.26-.66-2.6-.66-4s.24-2.74.66-4V13.62H5.1C3.56 16.66 2.68 20.2 2.68 24s.88 7.34 2.42 10.38l7.08-5.46v-1.84z" transform="scale(1)" />
                    <path fill="#EA4335" d="M24 9.6c3.12 0 5.88 1.08 8.08 3.16l6.06-6.06C34.48 3.18 29.7 1 24 1 15.78 1 8.56 5.58 5.1 13.62l7.08 5.46c1.66-4.98 6.32-9.48 11.82-9.48z" transform="scale(1)" />
                  </g>
                </svg>
              </motion.div>

              {/* Title with gradient underline */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-8"
              >
                <h1 className="text-4xl md:text-5xl font-medium text-[#202124] mb-4 tracking-tight">
                  Google Summer of Code
                </h1>
                <motion.div 
                  className="h-1 w-32 mx-auto rounded-full bg-gradient-to-r from-[#4285F4] via-[#FBBC04] to-[#34A853]"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                />
                <div className="flex items-center justify-center gap-3 mt-4">
                  <motion.span 
                    className="px-5 py-2 rounded-full bg-gradient-to-r from-[#4285F4] to-[#1A73E8] text-white text-sm font-medium shadow-lg shadow-[#4285F4]/25"
                    whileHover={{ scale: 1.05 }}
                  >
                    2020 Contributor
                  </motion.span>
                </div>
              </motion.div>

              {/* Enhanced Material Design Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, type: "spring" }}
                className="w-full max-w-md bg-white rounded-3xl overflow-hidden"
                style={{
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                }}
              >
                {/* Gradient header bar */}
                <div className="h-2 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853]" />
                
                <div className="p-6">
                  {/* Organization Header with icon */}
                  <div className="flex items-center gap-4 mb-6 pb-4 border-b border-[#E8EAED]">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#4285F4] to-[#1A73E8] flex items-center justify-center shadow-lg shadow-[#4285F4]/30">
                      <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 14l9-5-9-5-9 5 9 5z"/>
                        <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" opacity="0.7"/>
                      </svg>
                    </div>
                    <div className="text-left">
                      <h2 className="text-xl font-semibold text-[#202124]">Submitty</h2>
                      <p className="text-sm text-[#5F6368]">Rensselaer Polytechnic Institute</p>
                    </div>
                  </div>

                  {/* Project Info with icons */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#E8F0FE] flex items-center justify-center shrink-0">
                        <svg className="w-4 h-4 text-[#1A73E8]" fill="currentColor" viewBox="0 0 20 20"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>
                      </div>
                      <div className="text-left">
                        <p className="text-xs text-[#5F6368] uppercase tracking-wider mb-0.5">Project</p>
                        <p className="text-[#202124] font-medium">Peer Grading System</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#E6F4EA] flex items-center justify-center shrink-0">
                        <svg className="w-4 h-4 text-[#137333]" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/></svg>
                      </div>
                      <div className="text-left">
                        <p className="text-xs text-[#5F6368] uppercase tracking-wider mb-0.5">Contribution</p>
                        <p className="text-[#202124] text-sm">Built blind-review evaluation infrastructure for course assignments</p>
                      </div>
                    </div>
                  </div>

                  {/* Stats Pills with hover effects */}
                  <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-[#E8EAED]">
                    <motion.span 
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E6F4EA] text-[#137333] text-xs font-medium cursor-default"
                      whileHover={{ scale: 1.05 }}
                    >
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 16 16"><path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.751.751 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25Z"/></svg>
                      721 Stars
                    </motion.span>
                    <motion.span 
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E8F0FE] text-[#1A73E8] text-xs font-medium cursor-default"
                      whileHover={{ scale: 1.05 }}
                    >
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 16 16"><path d="M5 3.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm0 2.122a2.25 2.25 0 1 0-1.5 0v.878A2.25 2.25 0 0 0 5.75 8.5h1.5v2.128a2.251 2.251 0 1 0 1.5 0V8.5h1.5a2.25 2.25 0 0 0 2.25-2.25v-.878a2.25 2.25 0 1 0-1.5 0v.878a.75.75 0 0 1-.75.75h-4.5A.75.75 0 0 1 5 6.25v-.878Z"/></svg>
                      Open Source
                    </motion.span>
                    <motion.span 
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FCE8E6] to-[#FEEFC3] text-[#C5221F] text-xs font-medium cursor-default"
                      whileHover={{ scale: 1.05 }}
                    >
                      🏆 Top 1%
                    </motion.span>
                  </div>
                </div>
              </motion.div>

              {/* Footer with animated text */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="mt-8 flex items-center gap-2 text-[#5F6368] text-sm"
              >
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-[#34A853]"
                >
                  ✓
                </motion.span>
                Selected from 6,600+ applicants worldwide
              </motion.div>

            </div>
          </div>
        );

      // Slide 5: Founding Engineer - Plum (Bold Startup Story)
      case 'founding':
        return (
          <div className="min-h-screen bg-gradient-to-br from-[#0f0f23] via-[#1a1a2e] to-[#16213e] flex flex-col items-center justify-center px-4 py-8 text-center relative overflow-hidden">
            {/* Animated gradient mesh */}
            <div className="absolute inset-0">
              <motion.div
                className="absolute top-0 left-0 w-full h-full"
                style={{
                  background: 'radial-gradient(circle at 20% 20%, rgba(232, 90, 113, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(59, 179, 169, 0.15) 0%, transparent 50%)',
                }}
                animate={{
                  background: [
                    'radial-gradient(circle at 20% 20%, rgba(232, 90, 113, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(59, 179, 169, 0.15) 0%, transparent 50%)',
                    'radial-gradient(circle at 80% 20%, rgba(232, 90, 113, 0.15) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(59, 179, 169, 0.15) 0%, transparent 50%)',
                  ],
                }}
                transition={{ duration: 8, repeat: Infinity, repeatType: 'reverse' }}
              />
            </div>

            {/* Grid pattern */}
            <div
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: 'linear-gradient(#E85A71 1px, transparent 1px), linear-gradient(90deg, #E85A71 1px, transparent 1px)',
                backgroundSize: '60px 60px'
              }}
            />

            {/* Plum Logo - Glowing */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring' }}
              className="relative mb-2"
            >
              <span className="text-5xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-[#E85A71] to-[#ff8a9b]" style={{ textShadow: '0 0 60px rgba(232, 90, 113, 0.5)' }}>
                plum
              </span>
              <motion.div
                className="absolute -inset-4 bg-[#E85A71]/20 rounded-full blur-2xl -z-10"
                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
            </motion.div>

            {/* Title with badge */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="text-white text-2xl font-bold">Founding Engineer</span>
                <span className="px-2 py-0.5 bg-[#E85A71] text-white text-[10px] font-bold rounded-full">DAY 0</span>
              </div>
              <p className="text-white/40 text-sm">Joined 2021 • Early Team</p>
            </motion.div>

            {/* Big Impact Numbers */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-3 gap-4 mb-6 w-full max-w-sm"
            >
              {[
                { value: '0→6K', label: 'Enterprises', suffix: '+', color: '#E85A71' },
                { value: '600k+', label: 'Lives Insured', suffix: '', color: '#3BB3A9' },
                { value: 'Series A', label: 'Raised', suffix: '', color: '#F5A623', small: true },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  className="relative group"
                >
                  <div className="absolute inset-0 rounded-2xl opacity-20" style={{ backgroundColor: stat.color }} />
                  <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-3">
                    <div className={`font-black ${stat.small ? 'text-lg' : 'text-2xl'}`} style={{ color: stat.color }}>
                      {stat.value}{stat.suffix}
                    </div>
                    <p className="text-white/40 text-[9px] mt-1">{stat.label}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Journey Timeline - Vertical */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="relative w-full max-w-xs"
            >
              {/* Vertical line */}
              <motion.div
                className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#E85A71] via-[#3BB3A9] to-[#F5A623]"
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                style={{ transformOrigin: 'top' }}
              />

              {[
                { icon: '🚀', title: 'Day 0', desc: 'Owned entire stack', color: '#E85A71' },
                { icon: '⚡', title: 'Velocity', desc: 'POC → GTM → Ship', color: '#3BB3A9' },
                { icon: '🔥', title: 'Scale', desc: 'Found melting points', color: '#F5A623' },
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 + i * 0.15 }}
                  className="flex items-center gap-4 mb-3 last:mb-0"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-xl relative z-10 shrink-0"
                    style={{ backgroundColor: `${step.color}20`, boxShadow: `0 0 20px ${step.color}30` }}
                  >
                    {step.icon}
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold text-sm">{step.title}</p>
                    <p className="text-white/40 text-xs">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Quote */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4 }}
              className="text-white/30 text-xs mt-6 italic max-w-xs"
            >
              "Made mistakes at velocity. Learned systems thinking the hard way."
            </motion.p>

            {/* Bottom badge */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6 }}
              className="absolute bottom-6 flex items-center gap-2"
            >
              <span className="text-[#E85A71] text-lg">❤️</span>
              <span className="text-white/30 text-xs">Health Insurance for India</span>
            </motion.div>
          </div>
        );

      // Slide 6: Global Footprint - "Authentic Passport Stamps" Design
      case 'advisory':
        return (
          <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-6 py-10 text-center relative overflow-hidden">
            {/* Background: Vintage Map Texture */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {/* Subtle radial gradients */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(251,191,36,0.06)_0%,_transparent_50%)]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,_rgba(59,130,246,0.04)_0%,_transparent_50%)]" />
              
              {/* Flight path lines */}
              <svg className="absolute inset-0 w-full h-full opacity-[0.08]" viewBox="0 0 100 100" preserveAspectRatio="none">
                <motion.path 
                  d="M 5,25 Q 30,5 55,30 T 95,35" 
                  stroke="#F59E0B" 
                  strokeWidth="0.2" 
                  fill="none"
                  strokeDasharray="1,2"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 4, delay: 0.5 }}
                />
                <motion.path 
                  d="M 10,75 Q 40,55 70,70 T 95,60" 
                  stroke="#3B82F6" 
                  strokeWidth="0.2" 
                  fill="none"
                  strokeDasharray="1,2"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 4, delay: 1 }}
                />
              </svg>

              {/* Corner decorative stamps */}
              <div className="absolute top-6 left-6 w-20 h-20 border-2 border-amber-500/10 rounded-full flex items-center justify-center rotate-[-15deg]">
                <div className="w-14 h-14 border border-dashed border-amber-500/15 rounded-full flex items-center justify-center">
                  <span className="text-amber-500/20 text-[8px] font-mono">VERIFIED</span>
                </div>
              </div>
              <div className="absolute bottom-6 right-6 w-16 h-16 border-2 border-blue-500/10 rounded-full flex items-center justify-center rotate-[20deg]">
                <div className="w-10 h-10 border border-dashed border-blue-500/15 rounded-full" />
              </div>
            </div>

            {/* Main Content */}
            <div className="relative z-10 w-full max-w-5xl flex flex-col items-center">
              
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8"
              >
                <motion.span 
                  className="text-4xl mb-4 block"
                  animate={{ rotateY: [0, 360] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                >
                  🌏
                </motion.span>
                <h1 className="text-4xl md:text-5xl font-black text-white mb-3 tracking-tight">
                  Global <span className="text-amber-400">Footprint</span>
                </h1>
                <p className="text-neutral-400 text-base font-light max-w-xl mx-auto leading-relaxed">
                  Collaborated with engineering teams across <span className="text-amber-400 font-medium">multiple countries</span> on products used by <span className="text-amber-400 font-medium">10M+ users</span>
                </p>
              </motion.div>

              {/* Authentic Mini Postcards - Vintage Airmail Style */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 w-full mb-8">
                {[
                  { 
                    flag: '🇸🇬',
                    country: 'SINGAPORE',
                    tagline: 'DevOps Education Platform',
                    desc: 'Serving 1M+ engineers globally',
                    year: '2024',
                    inkColor: '#b91c1c',
                    borderColors: ['#ef4444', '#ffffff'], // Red/White
                    rotate: -2
                  },
                  { 
                    flag: '🇮🇩',
                    country: 'INDONESIA',
                    tagline: 'Insurtech Platform',
                    desc: 'Serving 9M+ users in SEA',
                    year: '2023',
                    inkColor: '#047857',
                    borderColors: ['#ef4444', '#ffffff'], // Red/White
                    rotate: 1
                  },
                  { 
                    flag: '🇹🇭',
                    country: 'THAILAND',
                    tagline: 'GenAI Agent Interfaces',
                    desc: 'For 50k+ insurance agents',
                    year: '2023',
                    inkColor: '#7c3aed',
                    borderColors: ['#ef4444', '#ffffff', '#1e3a8a'], // Red/White/Blue
                    rotate: -1
                  },
                  { 
                    flag: '🇮🇳',
                    country: 'INDIA',
                    tagline: 'Insurance Infrastructure',
                    desc: 'AI-first claims processing',
                    year: '2024',
                    inkColor: '#c2410c',
                    borderColors: ['#f97316', '#ffffff', '#16a34a'], // Orange/White/Green
                    rotate: 2
                  },
                  { 
                    flag: '🇦🇪',
                    country: 'UAE',
                    tagline: 'Global Travel Marketplace',
                    desc: 'Transformation for 5M+ MAUs',
                    year: '2024',
                    inkColor: '#1d4ed8',
                    borderColors: ['#ef4444', '#16a34a', '#ffffff', '#000000'], // Red/Green/White/Black
                    rotate: -1.5
                  },
                  { 
                    flag: '🇺🇸',
                    country: 'USA',
                    tagline: 'Academic Grading System',
                    desc: 'LMS for a New York University',
                    year: '2020',
                    inkColor: '#0e7490',
                    borderColors: ['#ef4444', '#ffffff', '#3b82f6'], // Red/White/Blue
                    rotate: 1.5
                  },
                  { 
                    flag: '🇮🇳',
                    country: 'INDIA',
                    tagline: 'Social Commerce Startup',
                    desc: 'Zero-to-one product engineering',
                    year: '2024',
                    inkColor: '#be185d',
                    borderColors: ['#f97316', '#ffffff', '#16a34a'], // Orange/White/Green
                    rotate: -2
                  },
                  { 
                    flag: '🇸🇬',
                    country: 'SINGAPORE',
                    tagline: 'Renewal Automation',
                    desc: 'High-scale backend systems',
                    year: '2023',
                    inkColor: '#b45309',
                    borderColors: ['#ef4444', '#ffffff'], // Red/White
                    rotate: 1
                  },
                ].map((stamp, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.9, rotate: -5 }}
                    animate={{ opacity: 1, scale: 1, rotate: stamp.rotate }}
                    transition={{ delay: 0.08 + i * 0.05, type: "spring", stiffness: 200 }}
                    whileHover={{ scale: 1.05, rotate: 0, zIndex: 10 }}
                    className="relative cursor-default group h-36 bg-[#fdfbf7] rounded overflow-hidden shadow-lg"
                  >
                    {/* Flag-Colored Airmail Border */}
                    <div 
                      className="absolute inset-0 pointer-events-none z-20 border-[6px] border-transparent"
                      style={{
                        background: `
                          repeating-linear-gradient(
                            135deg,
                            ${stamp.borderColors.map((color, idx) => 
                              `${color} ${idx * 10}px, ${color} ${(idx * 10) + 10}px`
                            ).join(', ')}
                          ) border-box
                        `,
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude'
                      }}
                    />

                    {/* Paper Texture */}
                    <div 
                      className="absolute inset-0 opacity-30 mix-blend-multiply z-0"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                      }}
                    />

                    {/* Postcard Content */}
                    <div className="relative z-10 p-3 h-full flex flex-col">
                      
                      {/* Top Row: Postage Stamp (Left) & Cancellation (Right) */}
                      <div className="flex justify-between items-start mb-1">
                        {/* Postage Stamp with Flag - Left */}
                        <span className="text-2xl">{stamp.flag}</span>

                        {/* Cancellation Mark / Ink Stamp - Right */}
                        <div className="w-16 h-16 opacity-70 mix-blend-multiply z-10 transform rotate-12 -mt-2 -mr-2">
                          <div 
                          className="w-12 h-10 bg-white shadow-sm flex items-center justify-center relative overflow-hidden"
                          style={{
                            maskImage: 'radial-gradient(circle at 2px 2px, transparent 2px, black 2.5px)',
                            maskSize: '6px 6px',
                            maskPosition: '-3px -3px'
                          }}
                        >
                        <svg viewBox="0 0 100 100" className="z-200 w-full h-full">
                            {/* Wavy lines */}
                            <path d="M0,40 Q25,30 50,40 T100,40" fill="none" stroke="#262626" strokeWidth="1" />
                            <path d="M0,50 Q25,40 50,50 T100,50" fill="none" stroke="#262626" strokeWidth="1" />
                            <path d="M0,60 Q25,50 50,60 T100,60" fill="none" stroke="#262626" strokeWidth="1" />
                            {/* Circle stamp part */}
                            <circle cx="50" cy="50" r="22" fill="none" stroke="#262626" strokeWidth="1" />
                            <text x="50" y="53" fontSize="8" textAnchor="middle" fill="#262626" fontFamily="serif" fontWeight="bold">
                              {stamp.country.substring(0, 3)}
                            </text>
                          </svg>
                          <div className="absolute bottom-0.5 right-0.5 text-[5px] font-bold text-neutral-400">
                            {stamp.year}
                          </div>
                        </div>
                        
                        </div>
                      </div>

                      {/* Middle Section: Text & Dotted Lines */}
                      <div className="flex flex-1 mt-1">
                        {/* Left: Typewriter Text */}
                        <div className="flex-1 pr-2 flex flex-col justify-end pb-1">
                          <div className="font-mono text-[10px] text-neutral-800 font-bold leading-tight mb-1">
                            {stamp.tagline}
                          </div>
                          <div className="font-mono text-[8px] text-neutral-500 leading-relaxed">
                            {stamp.desc}
                          </div>
                        </div>

                        {/* Right: Vertical Stack of Dotted Lines (Address Lines) */}
                        <div className="w-8 flex flex-col justify-end gap-1.5 pb-1 opacity-30">
                          <div className="h-[1px] w-full border-b border-dotted border-neutral-800" />
                          <div className="h-[1px] w-full border-b border-dotted border-neutral-800" />
                          <div className="h-[1px] w-full border-b border-dotted border-neutral-800" />
                          <div className="h-[1px] w-2/3 border-b border-dotted border-neutral-800" />
                        </div>
                      </div>

                      {/* Priority Sticker */}
                      <div className="absolute bottom-2 right-2 transform rotate-[-5deg]">
                        <div className="bg-blue-600 text-white text-[6px] font-bold px-1.5 py-0.5 uppercase tracking-wider shadow-sm">
                          AIR MAIL
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Footer stat */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="flex items-center gap-3 text-neutral-500 text-sm"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                <span className="font-light">Building products that scale globally</span>
              </motion.div>

            </div>
          </div>
        );

      // Slide 6.5: Unengineered Blog - "The Writer's Desk"
      case 'unengineered':
        return (
          <div className="min-h-screen bg-[#1a1a1a] flex flex-col items-center justify-center relative overflow-hidden">
            {/* Dark Wood Texture Background */}
            <div 
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='wood'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.05' numOctaves='3' result='noise'/%3E%3CfeDiffuseLighting in='noise' lighting-color='%233e2723' surfaceScale='2'%3E%3CfeDistantLight azimuth='45' elevation='60'/%3E%3C/feDiffuseLighting%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23wood)'/%3E%3C/svg%3E")`,
                backgroundSize: 'cover'
              }}
            />
            
            {/* Ambient Lighting */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80 pointer-events-none" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-amber-900/20 blur-[120px] rounded-full pointer-events-none" />

            {/* Content Container - The Desk */}
            <div className="relative z-10 w-full max-w-4xl px-6 flex flex-col items-center">
              
              {/* Header: Typewriter Paper */}
              <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="mb-12 relative"
              >
                <div className="bg-[#f0e6d2] text-neutral-900 px-8 py-6 shadow-2xl rotate-1 transform origin-top-left relative">
                  {/* Paper Texture */}
                  <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/paper.png')] mix-blend-multiply" />
                  
                  <h2 className="text-4xl md:text-6xl font-serif font-bold tracking-tight mb-2 relative z-10">
                    Unengineered
                  </h2>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className="font-mono text-xs text-neutral-500 uppercase tracking-widest">Launched 2024</span>
                    <span className="w-1 h-1 rounded-full bg-neutral-400" />
                    <span className="font-mono text-xs text-neutral-500 uppercase tracking-widest">Simpler words on everyday engineering</span>
                  </div>
                  
                  {/* Tape */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-24 h-8 bg-white/30 backdrop-blur-sm rotate-2 shadow-sm border border-white/20" />
                </div>
              </motion.div>

              {/* The Moleskine Notebook */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0, rotateX: 20 }}
                animate={{ scale: 1, opacity: 1, rotateX: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="relative w-full max-w-2xl perspective-1000"
              >
                {/* Notebook Cover/Binding */}
                <div className="bg-[#111] rounded-lg shadow-2xl overflow-hidden border-l-8 border-[#0a0a0a] relative">
                  {/* Elastic Band */}
                  <div className="absolute right-8 top-0 bottom-0 w-3 bg-[#0a0a0a] shadow-inner z-20" />
                  
                  {/* Pages */}
                  <div className="bg-[#fcfbf9] min-h-[400px] p-8 md:p-12 relative">
                    {/* Page Texture & Lines */}
                    <div className="absolute inset-0 opacity-[0.03]" 
                      style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px)', backgroundSize: '100% 24px' }} 
                    />
                    
                    {/* Blog Entries */}
                    <div className="space-y-8 relative z-10 font-serif">
                      {[
                        { 
                          title: "The Banana Context Theory", 
                          date: "July '25", 
                          desc: "AI context windows behave exactly like bananas. Not metaphorically." 
                        },
                        { 
                          title: "Etiquette of a Good Agent", 
                          date: "Nov '25", 
                          desc: "Most agents barge into the system like toddlers with API keys." 
                        },
                        { 
                          title: "I played with GPT OSS", 
                          date: "Aug '25", 
                          desc: "Downloaded them, broke them, fixed them, and tried to understand why." 
                        }
                      ].map((post, i) => (
                        <motion.div
                          key={i}
                          initial={{ x: -20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: 0.6 + i * 0.2 }}
                          className="block group cursor-default"
                        >
                          <div className="flex items-baseline justify-between border-b border-neutral-200 pb-2 mb-2 group-hover:border-neutral-400 transition-colors">
                            <h3 className="text-xl md:text-2xl font-medium text-neutral-800 group-hover:text-black transition-colors">
                              {post.title}
                            </h3>
                            <span className="font-mono text-xs text-neutral-400">{post.date}</span>
                          </div>
                          <p className="text-neutral-500 text-sm italic font-handwriting">
                            {post.desc}
                          </p>
                        </motion.div>
                      ))}
                    </div>

                    {/* Handwritten Note */}
                    <div className="absolute bottom-8 left-12 transform -rotate-2">
                      <span className="font-handwriting text-blue-700 text-lg opacity-80">
                        Search "Unengineered" on Substack
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Decorative Elements */}
              {/* Coffee Cup */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 }}
                className="absolute -right-4 top-20 hidden md:block"
              >
                <div className="w-24 h-24 rounded-full bg-[#f0f0f0] shadow-xl flex items-center justify-center border-4 border-white relative">
                  <div className="w-20 h-20 rounded-full bg-[#3c2f2f] shadow-inner relative overflow-hidden">
                    {/* Coffee Liquid */}
                    <div className="absolute inset-0 bg-[#4a3b3b] opacity-50" />
                    {/* Steam Animation */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 blur-xl animate-pulse" />
                  </div>
                  {/* Handle */}
                  <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-4 h-12 border-4 border-white rounded-r-xl" />
                </div>
              </motion.div>

              {/* Polaroid */}
              <motion.div
                initial={{ opacity: 0, rotate: 10, scale: 0.8 }}
                animate={{ opacity: 1, rotate: 6, scale: 1 }}
                transition={{ delay: 1.2 }}
                className="absolute -left-8 bottom-10 hidden md:block"
              >
                <div className="bg-white p-3 pb-8 shadow-lg transform rotate-6 w-40">
                  <div className="bg-neutral-200 h-32 w-full overflow-hidden grayscale contrast-125">
                     <img src="https://images.unsplash.com/photo-1455390582262-044cdead277a?w=400&q=80" alt="Desk" className="object-cover w-full h-full" />
                  </div>
                  <div className="font-handwriting text-center mt-2 text-xs text-neutral-500">
                    Workspace '24
                  </div>
                </div>
              </motion.div>

            </div>
          </div>
        );

      // Slide 7: Visa Scale - Official Visa branding (Blue #1A1F71 + Gold #F7B600)
      case 'scale':
        return (
          <div className="min-h-screen bg-gradient-to-br from-[#1A1F71] via-[#15195a] to-[#0d1040] flex flex-col items-center justify-center px-6 text-center relative overflow-hidden">
            {/* Visa-style diagonal stripe */}
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute -right-20 top-0 w-[200px] h-full bg-gradient-to-b from-[#F7B600]/20 via-[#F7B600]/10 to-transparent"
                style={{ transform: 'skewX(-15deg)' }}
              />
            </div>

            {/* Subtle grid */}
            <div
              className="absolute inset-0 opacity-[0.02]"
              style={{
                backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)',
                backgroundSize: '40px 40px'
              }}
            />

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-[#F7B600]/60 text-sm mb-6"
            >
              2024
            </motion.p>

            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: 'spring' }}
              className="mb-8 relative z-10"
            >
              {/* Visa wordmark style */}
              <div className="text-6xl sm:text-8xl font-black tracking-tight" style={{ fontStyle: 'italic' }}>
                <span className="text-white">V</span>
                <span className="text-white">I</span>
                <span className="text-white">S</span>
                <span className="text-[#F7B600]">A</span>
              </div>
              <p className="text-white/50 text-sm mt-2">Senior Software Engineer</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="grid grid-cols-2 gap-8 w-full max-w-xs relative z-10"
            >
              <div className="text-center bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1, type: 'spring' }}
                  className="text-4xl font-black text-[#F7B600]"
                >
                  1B+
                </motion.div>
                <p className="text-white/50 text-xs mt-1">transactions / year</p>
              </div>
              <div className="text-center bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1.2, type: 'spring' }}
                  className="text-4xl font-black text-[#F7B600]"
                >
                  400K+
                </motion.div>
                <p className="text-white/50 text-xs mt-1">merchants</p>
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6 }}
              className="text-white/30 text-xs mt-12"
            >
              Payment gateway infrastructure
            </motion.p>
          </div>
        );

      // Slide 7: Arctic Vault
      case 'vault':
        return (
          <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0d1f3c] to-[#0a1628] flex flex-col items-center justify-center px-6 text-center relative overflow-hidden">
            {/* Snowflakes */}
            {[...Array(20)].map((_, i) => (
              <Snowflake
                key={i}
                delay={i * 0.3}
                duration={4 + Math.random() * 3}
                left={Math.random() * 100}
              />
            ))}

            {/* Aurora effect */}
            <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-blue-500/10 via-cyan-400/5 to-transparent" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative z-10"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: 'spring' }}
                className="mb-6"
              >
                {/* Arctic badge */}
                <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-400/20 rounded-full px-4 py-2 mb-6">
                  <svg className="w-5 h-5 text-blue-300" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 4a4 4 0 100 8 4 4 0 000-8zM3 8a5 5 0 1110 0A5 5 0 013 8z"/>
                    <path d="M8 0a1 1 0 011 1v1a1 1 0 01-2 0V1a1 1 0 011-1zm0 12a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM1 7a1 1 0 100 2H2a1 1 0 100-2H1zm12 0a1 1 0 100 2h1a1 1 0 100-2h-1z"/>
                  </svg>
                  <span className="text-blue-200 text-sm font-medium">Arctic Code Vault</span>
                </div>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-3xl sm:text-4xl font-bold text-white mb-4"
              >
                My code was preserved
                <br />
                <span className="text-blue-300">for 1,000 years</span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
                className="text-blue-200/50 text-sm mb-8"
              >
                Archived in Svalbard, Norway
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="flex justify-center gap-6"
              >
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">152</div>
                  <p className="text-blue-300/50 text-xs">repositories</p>
                </div>
                <div className="w-px h-12 bg-blue-400/20" />
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">2017</div>
                  <p className="text-blue-300/50 text-xs">since</p>
                </div>
              </motion.div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8 }}
              className="absolute bottom-8 text-blue-300/30 text-xs"
            >
              GitHub Archive Program
            </motion.p>
          </div>
        );

      // Slide 8: Stage / Speaking - Kinetic Poster (Wrapped Vibe)
      case 'stage':
        return (
          <div className="min-h-screen bg-[#F3F4F6] flex flex-col items-center justify-center px-4 py-8 text-center relative overflow-hidden font-sans">
            {/* Background: High Energy Noise */}
            <div className="absolute inset-0 opacity-[0.15] bg-[url('https://grainy-gradients.vercel.app/noise.svg')] brightness-100 contrast-150 pointer-events-none" />
            
            {/* Dynamic Ticker Border */}
            <div className="absolute inset-0 border-[20px] border-transparent pointer-events-none z-20 overflow-hidden">
               <div className="absolute top-0 left-0 right-0 h-6 bg-black flex items-center overflow-hidden">
                  <motion.div 
                    className="flex whitespace-nowrap gap-4"
                    animate={{ x: [0, -1000] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                  >
                    {[...Array(10)].map((_, i) => (
                      <span key={i} className="text-white text-xs font-black tracking-widest uppercase">
                       FIRESIDE CHATS • CONFERENCES • TECH TALKS • SESSIONS • LIVE DEMOS • LAUNCHES
                      </span>
                    ))}
                  </motion.div>
               </div>
               <div className="absolute bottom-0 left-0 right-0 h-6 bg-black flex items-center overflow-hidden">
                  <motion.div 
                    className="flex whitespace-nowrap gap-4"
                    animate={{ x: [-1000, 0] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                  >
                    {[...Array(10)].map((_, i) => (
                      <span key={i} className="text-white text-xs font-black tracking-widest uppercase">
                        APPLIED AI • MCP • ENGINEERING • DESIGN • ANDROID • OPEN SOURCE •
                      </span>
                    ))}
                  </motion.div>
               </div>
            </div>

            {/* Main Content: The Poster */}
            <div className="relative z-10 w-full max-w-5xl h-full max-h-[80vh] flex flex-col justify-center">
              
              {/* Header Stomp */}
              <motion.div
                initial={{ scale: 2, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="mb-8"
              >
                <h2 className="text-[5rem] sm:text-[8rem] leading-[0.8] font-black text-black tracking-tighter uppercase transform -rotate-2 mix-blend-multiply">
                  The<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Stage</span>
                </h2>
              </motion.div>

              {/* Event Collage (Bento Box) */}
              <div className="grid grid-cols-12 grid-rows-2 gap-4 w-full h-[400px]">
                
                {/* Event 1: Massive */}
                <motion.div 
                  initial={{ y: 100, opacity: 0, rotate: 5 }}
                  animate={{ y: 0, opacity: 1, rotate: -2 }}
                  transition={{ delay: 0.2 }}
                  className="col-span-12 sm:col-span-7 bg-black text-white p-6 flex flex-col justify-between transform hover:scale-[1.02] transition-transform shadow-[10px_10px_0px_rgba(0,0,0,0.2)]"
                >
                  <div className="flex justify-between items-start">
                    <span className="bg-white text-black px-2 py-1 text-xs font-bold uppercase">Nov 24</span>
                    <span className="text-4xl">🎤</span>
                  </div>
                  <div>
                    <h3 className="text-6xl font-black tracking-tighter leading-none mb-2">CLOUDX</h3>
                    <p className="text-neutral-400 font-mono text-sm uppercase">Santa Clara, CA</p>
                  </div>
                </motion.div>

                {/* Event 2: Vertical */}
                <motion.div 
                  initial={{ x: 100, opacity: 0, rotate: -5 }}
                  animate={{ x: 0, opacity: 1, rotate: 2 }}
                  transition={{ delay: 0.3 }}
                  className="col-span-6 sm:col-span-5 bg-blue-600 text-white p-6 flex flex-col justify-center items-center text-center transform hover:scale-[1.02] transition-transform shadow-[10px_10px_0px_rgba(0,0,0,0.2)]"
                >
                  <span className="text-xs font-bold uppercase mb-2 opacity-80">Europe</span>
                  <h3 className="text-4xl sm:text-5xl font-black tracking-tighter leading-none">DEVELOPER<br/>WEEK</h3>
                </motion.div>

                {/* Event 3: Wide */}
                <motion.div 
                  initial={{ y: 100, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="col-span-6 sm:col-span-5 bg-white border-4 border-black text-black p-6 flex flex-col justify-center transform hover:scale-[1.02] transition-transform shadow-[10px_10px_0px_rgba(0,0,0,0.2)]"
                >
                  <h3 className="text-3xl font-black tracking-tighter uppercase">GDG Cloud</h3>
                </motion.div>

                {/* Stats Block */}
                <motion.div 
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.5, type: "spring" }}
                  className="col-span-12 sm:col-span-7 bg-[#FA923E] text-white p-4 flex items-center justify-around shadow-[10px_10px_0px_rgba(0,0,0,0.2)]"
                >
                  <h3 className="text-3xl font-black tracking-tighter uppercase">CS FIRST</h3>
                </motion.div>

              </div>
            </div>
          </div>
        );

      // Slide 9: Ships (Mission Control) - Scanning UX
      case 'ships': {
        const activeProject = shippedProjects[scanIndex] ?? shippedProjects[0];
        const scanFraction = shippedProjects.length
          ? Math.min((scanIndex + 1) / shippedProjects.length, 1)
          : 0;
        const progressPercent = Math.round(scanFraction * 100);
        const scanLabel = String(Math.min(scanIndex + 1, shippedProjects.length)).padStart(2, '0');
        const totalLabel = String(shippedProjects.length).padStart(2, '0');

        return (
          <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-4 py-8 text-center relative overflow-hidden font-mono">
            {/* Background Grid */}
            <div
              className="absolute inset-0 opacity-[0.1]"
              style={{
                backgroundImage: 'linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)',
                backgroundSize: '40px 40px'
              }}
            />

            {/* Scanner UI */}
            <AnimatePresence mode="wait">
               <motion.div
                  key="scanner"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.9, filter: "blur(12px)" }}
                  className="relative z-10 flex flex-col items-center gap-12 w-full"
                >
                  <div className="flex flex-col items-center gap-3 text-center">
                    <span className="text-[18px] font-mono uppercase tracking-[0.4em] text-emerald-400/70">
                      Shipped more than I expected
                    </span>
                  </div>

                  <div className="relative w-full max-w-sm sm:max-w-l aspect-square scale-95">
                    <div className="absolute inset-[-10%] rounded-full bg-emerald-500/10 blur-[140px] opacity-50" />
                    <div className="absolute inset-[-12%] rounded-full border border-emerald-400/20 opacity-40" />
                    <div
                      className="absolute inset-0 opacity-[0.12]"
                      style={{
                        backgroundImage:
                          'linear-gradient(#163128 1px, transparent 1px), linear-gradient(90deg, #163128 1px, transparent 1px)',
                        backgroundSize: '40px 40px'
                      }}
                    />
                    <div className="absolute inset-0 rounded-full border border-emerald-400/40 shadow-[0_0_80px_rgba(16,185,129,0.35)]" />
                    <div className="absolute inset-[12%] rounded-full border border-emerald-400/20" />
                    <div className="absolute inset-[26%] rounded-full border border-emerald-400/15" />
                    <div className="absolute inset-[40%] rounded-full border border-emerald-400/10" />
                    <div className="absolute inset-[54%] rounded-full border border-emerald-400/5" />

                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-full h-px bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent" />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-full w-px bg-gradient-to-b from-transparent via-emerald-400/30 to-transparent" />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center rotate-45">
                      <div className="w-full h-px bg-gradient-to-r from-transparent via-emerald-400/15 to-transparent" />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center -rotate-45">
                      <div className="w-full h-px bg-gradient-to-r from-transparent via-emerald-400/15 to-transparent" />
                    </div>

                    <motion.div
                      className="absolute inset-[6%] rounded-full"
                      style={{
                        background:
                          'conic-gradient(from 0deg, rgba(16,185,129,0.45) 0deg, rgba(16,185,129,0.08) 120deg, transparent 250deg)'
                      }}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 3.6, repeat: Infinity, ease: "linear" }}
                    />
                    <motion.div
                      className="absolute inset-[10%] rounded-full border border-emerald-400/10"
                      animate={{ rotate: [0, 6, 0, -6, 0] }}
                      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <motion.div
                      className="absolute inset-[18%] rounded-full border border-emerald-400/10"
                      animate={{ opacity: [0.1, 0.3, 0.1] }}
                      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <div className="absolute inset-[45%] rounded-full bg-emerald-500/30 blur-[50px] opacity-20" />
                    <div className="absolute inset-[47%] rounded-full border border-emerald-300/30" />
                    <div className="absolute inset-[48%] rounded-full bg-emerald-500/30 opacity-30" />

                    <div className="relative z-20 w-full h-full">
                      {shippedProjects.map((project, i) => {
                        const angleDeg = (i * 30 + 6) % 360;
                        const angle = angleDeg * (Math.PI / 180);
                        const ring = (i % 4) + 1;
                        const radius = 15 + ring * 12;
                        const labelRadius = radius + 10;
                        const dotLeft = 50 + Math.cos(angle) * radius;
                        const dotTop = 50 + Math.sin(angle) * radius;
                        const labelLeft = 50 + Math.cos(angle) * labelRadius;
                        const labelTop = 50 + Math.sin(angle) * labelRadius;
                        const isActive = scanIndex === i && !scanComplete;

                        return (
                          <React.Fragment key={project.name}>
                            <motion.div
                              className="absolute"
                              style={{
                                left: `${dotLeft}%`,
                                top: `${dotTop}%`,
                                transform: 'translate(-50%, -50%)'
                              }}
                              initial={{ opacity: 0, scale: 0.6 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.3 + i * 0.05, duration: 0.4 }}
                            >
                              {isActive && (
                                <motion.span
                                  className="absolute -inset-5 rounded-full border border-emerald-400/40"
                                  animate={{ opacity: [0.6, 0], scale: [0.7, 1.7] }}
                                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
                                />
                              )}
                              <span
                                className={`relative block w-2 h-2 rounded-full shadow-[0_0_12px_rgba(16,185,129,0.9)] ${
                                  isActive ? 'bg-emerald-200' : 'bg-emerald-400/80'
                                }`}
                              />
                            </motion.div>

                            <motion.div
                              className="absolute"
                              style={{
                                left: `${labelLeft}%`,
                                top: `${labelTop}%`,
                                transform: 'translate(-50%, -50%)'
                              }}
                              initial={{ opacity: 0, y: 6 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.5 + i * 0.05, duration: 0.4 }}
                            >
                              <div className="relative">
                                <div className="absolute inset-0 blur-lg bg-emerald-400/10" />
                                <div
                                  className={`relative px-3 py-1 rounded-full border border-emerald-500/30 bg-black/60 backdrop-blur-md font-mono text-[10px] uppercase tracking-[0.3em] ${
                                    isActive ? 'text-emerald-200' : 'text-emerald-400/70'
                                  }`}
                                >
                                  {project.name}
                                </div>
                              </div>
                            </motion.div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="relative w-full max-w-xl text-left"
                  >
                    <div className="absolute inset-0 blur-[120px] bg-emerald-500/10" />
                    <div className="relative border border-emerald-500/20 bg-black/50 backdrop-blur-2xl rounded-[32px] px-8 py-6 shadow-[0_30px_80px_rgba(16,185,129,0.2)]">
                      <div className="mt-4 flex items-start justify-between gap-6">
                        <div className="flex-1">
                          <span className="block text-[10px] font-mono uppercase tracking-[0.35em] text-emerald-500/60">
                            Project
                          </span>
                          <span className="block mt-2 text-2xl font-semibold text-emerald-100 tracking-tight">
                            {activeProject?.name}
                          </span>
                          <span className="block mt-2 text-sm text-emerald-200/80 leading-relaxed">
                            {activeProject?.desc}
                          </span>
                        </div>
                        <div className="flex flex-col items-end gap-2 text-[10px] font-mono uppercase tracking-[0.35em] text-emerald-400/60">
                          <span>Status</span>
                          <span className="text-emerald-200">Locked</span>
                        </div>
                      </div>
                      <div className="relative mt-6 h-1.5 rounded-full bg-emerald-900/70 overflow-hidden">
                        <motion.div
                          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-200"
                          initial={{ width: '0%' }}
                          animate={{ width: `${progressPercent}%` }}
                          transition={{ duration: 0.35, ease: "easeOut" }}
                        />
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
            </AnimatePresence>
          </div>
        );
      }

      // Slide 10: Ultrathink Wrap Line
      case 'proof':
        return (
          <div className="min-h-screen bg-black relative flex flex-col items-center justify-center overflow-hidden px-6 py-20 text-center">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,#05090f_0%,#010203_100%)]" />
            <div className="absolute inset-0 opacity-[0.15] bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />

            <motion.div
              className="absolute inset-auto w-[620px] max-w-[80vw] h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent"
              initial={{ opacity: 0, scaleX: 0.3 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />

            <motion.div
              className="relative z-10 flex flex-col items-center gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <div className="flex items-center gap-6 text-white/80 text-2xl sm:text-3xl lg:text-4xl font-light tracking-[0.35em] uppercase">
                <span className="h-px w-16 bg-emerald-500/40" />
                <span>That's a wrap</span>
                <span className="h-px w-16 bg-emerald-500/40" />
              </div>
              <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-emerald-500/40">
                (for now!)
              </div>
            </motion.div>

            <motion.div
              className="absolute bottom-10 flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.35em] text-emerald-500/30"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
            </motion.div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen cursor-pointer select-none overflow-hidden"
      onClick={handleClick}
    >
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 flex gap-1 p-3 bg-gradient-to-b from-black/60 to-transparent">
        {slides.map((_, i) => (
          <div
            key={i}
            className="flex-1 h-1 rounded-full overflow-hidden bg-white/20"
          >
            <motion.div
              className="h-full bg-white"
              initial={{ width: '0%' }}
              animate={{
                width: i < currentSlide ? '100%' : i === currentSlide ? '100%' : '0%'
              }}
              transition={{ duration: i === currentSlide ? 5 : 0.3 }}
            />
          </div>
        ))}
      </div>

      {/* Close button */}
      <button
        onClick={(e) => { e.stopPropagation(); navigate('/'); }}
        className="fixed top-10 right-4 z-50 text-white/40 hover:text-white p-2 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Slide content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.3 }}
        >
          {renderSlide()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default LinkedInWrapped;
